import { uploadNotificationEmailImage } from "../../action/notification";

const EMAIL_FORMATTING_TAGS = [
  "b",
  "strong",
  "i",
  "em",
  "s",
  "strike",
  "del",
  "u",
  "br",
];

export const EMAIL_TEMPLATE_OPTIONS = [
  {
    id: "general-announcement",
    label: "General Announcement",
    subject: "Important update from MirAIe",
    html: `<p>Dear MirAIe User,</p>
<p>We would like to share an important update with you.</p>
<p><strong>[Add your announcement here]</strong></p>
<p>Thank you for being a valued member of the MirAIe community.</p>
<p>Best regards,<br/>MirAIe Team</p>`,
  },
  {
    id: "product-update",
    label: "Product Update",
    subject: "New MirAIe product update available",
    html: `<p>Dear MirAIe User,</p>
<p>We are excited to inform you about a new update to your MirAIe experience.</p>
<ul>
  <li><strong>Feature:</strong> [Describe the update]</li>
  <li><strong>Benefit:</strong> [Explain the value to users]</li>
</ul>
<p>Please open the MirAIe app to explore the latest improvements.</p>
<p>Best regards,<br/>MirAIe Team</p>`,
  },
  {
    id: "promotion",
    label: "Promotion",
    subject: "Exclusive offer for MirAIe users",
    html: `<p>Dear MirAIe User,</p>
<p>We have an exclusive offer just for you!</p>
<p style="text-align:center;"><strong>[Promotion title]</strong></p>
<p>[Promotion details and terms]</p>
<p>Best regards,<br/>MirAIe Team</p>`,
  },
  {
    id: "maintenance-notice",
    label: "Maintenance Notice",
    subject: "Scheduled MirAIe service maintenance",
    html: `<p>Dear MirAIe User,</p>
<p>Please be informed that scheduled maintenance will be performed on our services.</p>
<p><strong>Date:</strong> [Date]<br/><strong>Time:</strong> [Time window]<br/><strong>Impact:</strong> [Expected impact]</p>
<p>We apologize for any inconvenience and appreciate your understanding.</p>
<p>Best regards,<br/>MirAIe Team</p>`,
  },
];

const NOTIFICATION_EMAIL_IMAGE_THUMB_CLASS = "notification-email-image-thumb";
const NOTIFICATION_EMAIL_IMAGE_ITEM_CLASS = "notification-email-image-item";
const NOTIFICATION_EMAIL_IMAGE_REMOVE_CLASS = "notification-email-image-remove";

const isPublicImageUrl = (url = "") => /^https?:\/\/.+/i.test(String(url).trim());

const normalizePublicImageUrl = (url = "") => {
  const trimmed = String(url).trim();
  if (!trimmed) return "";
  if (trimmed.startsWith("//")) return `https:${trimmed}`;
  return trimmed;
};

const notificationImagePreviewUrls = new Map();
let notificationImagePreviewId = 0;

const registerNotificationImagePreviewUrl = (url) => {
  const id = `notification-image-${++notificationImagePreviewId}`;
  notificationImagePreviewUrls.set(id, url);
  return id;
};

export function resetNotificationEmailImageRegistry() {
  notificationImagePreviewUrls.clear();
  notificationImagePreviewId = 0;
}

const getElementAttribute = (element, name) => {
  if (!element?.getAttribute) return "";
  return element.getAttribute(name) || "";
};

const getNotificationImagePreviewUrl = (element) => {
  if (!element) return "";

  const id = getElementAttribute(element, "data-notification-image-id");
  if (id && notificationImagePreviewUrls.has(id)) {
    return notificationImagePreviewUrls.get(id) || "";
  }

  const savedSrc = getElementAttribute(element, "data-notification-image-src");
  const normalizedSaved = normalizePublicImageUrl(savedSrc);
  if (isPublicImageUrl(normalizedSaved)) return normalizedSaved;

  const src = normalizePublicImageUrl(getElementAttribute(element, "src"));
  return isPublicImageUrl(src) ? src : "";
};

// Match editor thumbnail size; table layout keeps dimensions in Gmail/Outlook/mobile clients.
const EMAIL_DELIVERY_IMAGE_MAX_WIDTH = 120;
const EMAIL_DELIVERY_IMAGE_MAX_HEIGHT = 80;

const createEmailDeliveryImage = (src, alt = "") => {
  const w = EMAIL_DELIVERY_IMAGE_MAX_WIDTH;
  const h = EMAIL_DELIVERY_IMAGE_MAX_HEIGHT;
  const normalizedSrc = normalizePublicImageUrl(src) || src;

  const img = document.createElement("img");
  img.setAttribute("src", normalizedSrc);
  img.setAttribute("alt", alt);
  img.setAttribute("width", String(w));
  img.setAttribute("border", "0");
  img.setAttribute(
    "style",
    `display:block;width:${w}px;max-width:${w}px;max-height:${h}px;height:auto;border:0;outline:none;`,
  );

  const link = document.createElement("a");
  link.setAttribute("href", normalizedSrc);
  link.setAttribute("target", "_blank");
  link.setAttribute("rel", "noopener noreferrer");
  link.setAttribute("title", "View full size image");
  link.setAttribute("style", "text-decoration:none;border:0;");
  link.appendChild(img);

  const td = document.createElement("td");
  td.setAttribute("width", String(w));
  td.setAttribute("valign", "top");
  td.setAttribute("style", `width:${w}px;padding:4px;vertical-align:top;`);
  td.appendChild(link);

  const tr = document.createElement("tr");
  tr.appendChild(td);

  const table = document.createElement("table");
  table.setAttribute("role", "presentation");
  table.setAttribute("cellpadding", "0");
  table.setAttribute("cellspacing", "0");
  table.setAttribute("border", "0");
  table.setAttribute("align", "left");
  table.setAttribute("data-email-delivery-image-table", "true");
  table.setAttribute(
    "style",
    "border-collapse:collapse;mso-table-lspace:0pt;mso-table-rspace:0pt;display:inline-table;margin:4px 4px 4px 0;",
  );
  table.appendChild(tr);

  return table;
};

const mergeAdjacentDeliveryImageTables = (container) => {
  const parents = new Set();
  container.querySelectorAll("table[data-email-delivery-image-table]").forEach((table) => {
    if (table.parentElement) parents.add(table.parentElement);
  });

  parents.forEach((parent) => {
    const children = [...parent.childNodes];
    let group = [];

    const flushGroup = () => {
      if (group.length <= 1) {
        group = [];
        return;
      }

      const mergedTable = document.createElement("table");
      mergedTable.setAttribute("role", "presentation");
      mergedTable.setAttribute("cellpadding", "0");
      mergedTable.setAttribute("cellspacing", "0");
      mergedTable.setAttribute("border", "0");
      mergedTable.setAttribute("data-email-delivery-image-table", "true");
      mergedTable.setAttribute(
        "style",
        "border-collapse:collapse;mso-table-lspace:0pt;mso-table-rspace:0pt;margin:4px 0;",
      );

      const tr = document.createElement("tr");
      group.forEach((table) => {
        const td = table.querySelector("td");
        if (td) tr.appendChild(td.cloneNode(true));
      });
      mergedTable.appendChild(tr);
      group[0].replaceWith(mergedTable);
      for (let i = 1; i < group.length; i += 1) {
        group[i].remove();
      }
      group = [];
    };

    children.forEach((node) => {
      if (node.nodeType === 1 && node.matches?.("table[data-email-delivery-image-table]")) {
        group.push(node);
        return;
      }
      flushGroup();
    });
    flushGroup();
  });
};

const cleanupEmptyEmailBlocks = (container) => {
  container.querySelectorAll("p").forEach((paragraph) => {
    const text = paragraph.textContent?.replace(/\u00a0/g, " ").trim();
    if (!text && !paragraph.querySelector("img") && !paragraph.querySelector("table[data-email-delivery-image-table]")) {
      paragraph.remove();
    }
  });
};

const cleanupDeliveryHtml = (container) => {
  container.querySelectorAll(`.${NOTIFICATION_EMAIL_IMAGE_REMOVE_CLASS}`).forEach((el) => el.remove());
  container.querySelectorAll(`.${NOTIFICATION_EMAIL_IMAGE_ITEM_CLASS}`).forEach((el) => el.remove());
  cleanupEmptyEmailBlocks(container);
};

const EMAIL_STRUCTURE_TAGS = ["p", "ul", "ol", "li"];
const ALLOWED_EMAIL_HTML_TAG_SET = new Set([
  ...EMAIL_FORMATTING_TAGS,
  ...EMAIL_STRUCTURE_TAGS,
  "img",
  "span",
]);

const ALLOWED_FONT_SIZE_VALUES = new Set([
  "small",
  "medium",
  "large",
  "x-small",
  "x-large",
  "smaller",
  "larger",
]);

const isSafeColorValue = (value) =>
  /^#[0-9a-f]{3,8}$/i.test(value) ||
  /^rgb\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}\s*\)$/i.test(value) ||
  /^rgba\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*(0|1|0?\.\d+)\s*\)$/i.test(value);

const sanitizeEmailInlineStyle = (styleStr) => {
  if (!styleStr?.trim()) return null;

  const safeDeclarations = styleStr
    .split(";")
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => {
      const colonIndex = part.indexOf(":");
      if (colonIndex === -1) return null;
      const property = part.slice(0, colonIndex).trim().toLowerCase();
      const value = part.slice(colonIndex + 1).trim().toLowerCase();
      if (!property || !value) return null;

      if (property === "color" && isSafeColorValue(value)) {
        return `color:${value}`;
      }
      if (property === "background-color" && isSafeColorValue(value)) {
        return `background-color:${value}`;
      }
      if (property === "font-size" && ALLOWED_FONT_SIZE_VALUES.has(value)) {
        return `font-size:${value}`;
      }
      if (property === "text-align" && ["left", "center", "right", "justify"].includes(value)) {
        return `text-align:${value}`;
      }
      return null;
    })
    .filter(Boolean);

  return safeDeclarations.length ? safeDeclarations.join(";") : null;
};

const hasEmailImageItemClass = (element, className) =>
  (element.getAttribute("class") || "").split(/\s+/).includes(className);

const isBoldStyle = (style = "") => /font-weight\s*:\s*(bold|[7-9]00)\b/i.test(style);
const isItalicStyle = (style = "") => /font-style\s*:\s*italic\b/i.test(style);
const isStrikeStyle = (style = "") => /text-decoration\s*:[^;]*line-through/i.test(style);

const promoteStyledSpanToSemanticTags = (span) => {
  if (
    hasEmailImageItemClass(span, NOTIFICATION_EMAIL_IMAGE_ITEM_CLASS)
    || hasEmailImageItemClass(span, NOTIFICATION_EMAIL_IMAGE_REMOVE_CLASS)
  ) {
    return false;
  }

  const style = span.getAttribute("style") || "";
  const flags = {
    bold: isBoldStyle(style),
    italic: isItalicStyle(style),
    strike: isStrikeStyle(style),
  };

  if (!flags.bold && !flags.italic && !flags.strike) {
    return false;
  }

  const fragment = document.createDocumentFragment();
  while (span.firstChild) {
    fragment.appendChild(span.firstChild);
  }

  let wrapped = fragment;
  const wrap = (tagName) => {
    const wrapper = document.createElement(tagName);
    wrapper.appendChild(wrapped);
    wrapped = wrapper;
  };

  if (flags.strike) wrap("s");
  if (flags.italic) wrap("i");
  if (flags.bold) wrap("b");

  span.replaceWith(wrapped);
  return true;
};

const EMAIL_BLOCK_TAGS = new Set(["p", "ul", "ol", "li", "div", "table", "blockquote"]);

const convertDivElementsToParagraphs = (root) => {
  [...root.querySelectorAll("div")].reverse().forEach((div) => {
    const hasNestedBlock = [...div.children].some((child) =>
      EMAIL_BLOCK_TAGS.has(child.tagName.toLowerCase()),
    );

    if (hasNestedBlock) {
      const parent = div.parentNode;
      if (!parent) return;
      while (div.firstChild) {
        parent.insertBefore(div.firstChild, div);
      }
      parent.removeChild(div);
      return;
    }

    const paragraph = document.createElement("p");
    const safeStyle = sanitizeEmailInlineStyle(div.getAttribute("style"));
    while (div.firstChild) {
      paragraph.appendChild(div.firstChild);
    }

    if (!paragraph.childNodes.length) {
      paragraph.appendChild(document.createElement("br"));
    }

    if (safeStyle) {
      paragraph.setAttribute("style", safeStyle);
    }

    div.replaceWith(paragraph);
  });
};

const normalizeEmailEditorDom = (root) => {
  if (!root) return;

  [...root.querySelectorAll("span")].reverse().forEach((span) => {
    promoteStyledSpanToSemanticTags(span);
  });

  convertDivElementsToParagraphs(root);

  [...root.querySelectorAll("font")].forEach((node) => {
    const parent = node.parentNode;
    if (!parent) return;
    while (node.firstChild) {
      parent.insertBefore(node.firstChild, node);
    }
    parent.removeChild(node);
  });
};

const sanitizeEmailImageElement = (img) => {
  const src = getNotificationImagePreviewUrl(img);
  const alt = img.getAttribute("alt") || "Image";
  const previewId = img.getAttribute("data-notification-image-id");

  [...img.attributes].forEach((attr) => img.removeAttribute(attr.name));
  if (!isPublicImageUrl(src)) {
    img.remove();
    return;
  }

  img.setAttribute("class", NOTIFICATION_EMAIL_IMAGE_THUMB_CLASS);
  img.setAttribute("src", src);
  img.setAttribute("alt", alt);
  img.setAttribute("data-notification-image-src", src);
  img.setAttribute("contenteditable", "false");
  img.setAttribute("title", "Click to preview image");
  if (previewId) {
    img.setAttribute("data-notification-image-id", previewId);
  }
};

const sanitizeEmailSpanElement = (span) => {
  if (hasEmailImageItemClass(span, NOTIFICATION_EMAIL_IMAGE_ITEM_CLASS)) {
    [...span.attributes].forEach((attr) => span.removeAttribute(attr.name));
    span.setAttribute("class", NOTIFICATION_EMAIL_IMAGE_ITEM_CLASS);
    span.setAttribute("contenteditable", "false");
    return;
  }

  if (hasEmailImageItemClass(span, NOTIFICATION_EMAIL_IMAGE_REMOVE_CLASS)) {
    [...span.attributes].forEach((attr) => span.removeAttribute(attr.name));
    span.setAttribute("class", NOTIFICATION_EMAIL_IMAGE_REMOVE_CLASS);
    span.setAttribute("contenteditable", "false");
    span.setAttribute("title", "Remove image");
    span.setAttribute("role", "button");
    span.setAttribute("aria-label", "Remove image");
    span.textContent = "\u00d7";
    return;
  }

  const safeStyle = sanitizeEmailInlineStyle(span.getAttribute("style"));
  [...span.attributes].forEach((attr) => span.removeAttribute(attr.name));
  if (safeStyle) {
    span.setAttribute("style", safeStyle);
    return;
  }

  const parent = span.parentNode;
  if (!parent) {
    span.remove();
    return;
  }
  while (span.firstChild) {
    parent.insertBefore(span.firstChild, span);
  }
  parent.removeChild(span);
};

export const sanitizeEmailNotificationHtml = (html = "") => {
  if (!html?.trim()) return "";

  if (typeof DOMParser === "undefined") {
    return html.trim();
  }

  const doc = new DOMParser().parseFromString(`<div>${html}</div>`, "text/html");
  const root = doc.body.firstChild;
  if (!root) return "";

  normalizeEmailEditorDom(root);

  const sanitizeNode = (node) => {
    [...node.childNodes].forEach((child) => {
      if (child.nodeType !== Node.ELEMENT_NODE) return;

      const tag = child.tagName.toLowerCase();
      if (!ALLOWED_EMAIL_HTML_TAG_SET.has(tag)) {
        sanitizeNode(child);
        const parent = child.parentNode;
        while (child.firstChild) {
          parent.insertBefore(child.firstChild, child);
        }
        parent.removeChild(child);
        return;
      }

      if (tag === "img") {
        sanitizeEmailImageElement(child);
        return;
      }

      if (tag === "span") {
        sanitizeEmailSpanElement(child);
        sanitizeNode(child);
        return;
      }

      if (tag === "p") {
        const safeStyle = sanitizeEmailInlineStyle(child.getAttribute("style"));
        [...child.attributes].forEach((attr) => child.removeAttribute(attr.name));
        if (safeStyle) {
          child.setAttribute("style", safeStyle);
        }
        sanitizeNode(child);
        return;
      }

      [...child.attributes].forEach((attr) => child.removeAttribute(attr.name));
      sanitizeNode(child);
    });
  };

  sanitizeNode(root);
  return root.innerHTML.trim();
};

const openNotificationImagePreview = (url) => {
  const normalized = normalizePublicImageUrl(url);
  if (!isPublicImageUrl(normalized)) return false;
  window.open(normalized, "_blank", "noopener,noreferrer");
  return true;
};

const focusAfterElement = (editorEl, element) => {
  if (!editorEl || !element) return;
  const selection = window.getSelection();
  if (!selection) return;

  const range = document.createRange();
  range.setStartAfter(element);
  range.collapse(true);
  selection.removeAllRanges();
  selection.addRange(range);
};

const createAttachedImagePreview = (url, displayName = "Image") => {
  const normalizedUrl = normalizePublicImageUrl(url);
  if (!isPublicImageUrl(normalizedUrl)) return null;

  const previewId = registerNotificationImagePreviewUrl(normalizedUrl);
  const img = document.createElement("img");
  img.setAttribute("class", NOTIFICATION_EMAIL_IMAGE_THUMB_CLASS);
  img.setAttribute("src", normalizedUrl);
  img.setAttribute("alt", displayName || "Image");
  img.setAttribute("data-notification-image-id", previewId);
  img.setAttribute("data-notification-image-src", normalizedUrl);
  img.setAttribute("contenteditable", "false");
  img.setAttribute("title", "Click to preview image");
  return img;
};

const createNotificationEmailImageRemoveButton = () => {
  const removeButton = document.createElement("span");
  removeButton.setAttribute("class", NOTIFICATION_EMAIL_IMAGE_REMOVE_CLASS);
  removeButton.setAttribute("contenteditable", "false");
  removeButton.setAttribute("title", "Remove image");
  removeButton.setAttribute("role", "button");
  removeButton.setAttribute("aria-label", "Remove image");
  removeButton.textContent = "\u00d7";
  return removeButton;
};

const createNotificationEmailImageItem = (previewElement) => {
  const item = document.createElement("span");
  item.setAttribute("class", NOTIFICATION_EMAIL_IMAGE_ITEM_CLASS);
  item.setAttribute("contenteditable", "false");
  item.append(previewElement);
  item.append(createNotificationEmailImageRemoveButton());
  return item;
};

const removeNotificationEmailImageFromDom = (editorEl, removeButtonEl, onContentChange) => {
  const item = removeButtonEl.closest(`.${NOTIFICATION_EMAIL_IMAGE_ITEM_CLASS}`);
  if (!item) return;

  const preview = item.querySelector(`img.${NOTIFICATION_EMAIL_IMAGE_THUMB_CLASS}`);
  const previewId = preview?.getAttribute("data-notification-image-id");
  if (previewId) {
    notificationImagePreviewUrls.delete(previewId);
  }

  item.remove();
  if (typeof onContentChange === "function") {
    onContentChange();
  }
};

export const insertImageIntoEditor = (editorEl, url, displayName = "Image", onContentChange) => {
  if (!editorEl || !url) return;

  const normalizedUrl = normalizePublicImageUrl(url);
  if (!isPublicImageUrl(normalizedUrl)) return;

  const preview = createAttachedImagePreview(normalizedUrl, displayName);
  if (!preview) return;

  const item = createNotificationEmailImageItem(preview);

  editorEl.focus();
  const selection = window.getSelection();
  if (selection?.rangeCount) {
    const range = selection.getRangeAt(0);
    if (editorEl.contains(range.commonAncestorContainer)) {
      range.deleteContents();
      range.insertNode(item);
      focusAfterElement(editorEl, item);
    } else {
      editorEl.appendChild(item);
    }
  } else {
    editorEl.appendChild(item);
  }

  if (typeof onContentChange === "function") {
    onContentChange();
  }
};

const countImageThumbsInHtml = (container) =>
  container.querySelectorAll(`img.${NOTIFICATION_EMAIL_IMAGE_THUMB_CLASS}`).length;

const replaceThumbWithDeliveryImage = (thumb, failed) => {
  const src = getNotificationImagePreviewUrl(thumb);
  const alt = thumb.getAttribute("alt") || "Image";

  if (!isPublicImageUrl(src)) {
    failed.push({
      alt,
      reason: "Attached images could not be read. Upload the image again.",
    });
    return null;
  }

  return createEmailDeliveryImage(src, alt);
};

const unwrapNestedParagraphs = (container) => {
  let nested = container.querySelector("p p");
  while (nested) {
    const inner = nested;
    const parent = inner.parentElement;
    if (parent?.tagName !== "P") break;
    while (inner.firstChild) {
      parent.insertBefore(inner.firstChild, inner);
    }
    inner.remove();
    nested = container.querySelector("p p");
  }
};

const transformEditorImagesForDelivery = (container) => {
  const failed = [];
  let imageCount = 0;

  [...container.querySelectorAll(`.${NOTIFICATION_EMAIL_IMAGE_ITEM_CLASS}`)].forEach((item) => {
    const thumb = item.querySelector(`img.${NOTIFICATION_EMAIL_IMAGE_THUMB_CLASS}`);
    if (!thumb) {
      item.remove();
      return;
    }

    const deliveryNode = replaceThumbWithDeliveryImage(thumb, failed);
    if (!deliveryNode) return;

    imageCount += 1;
    item.replaceWith(deliveryNode);
  });

  [...container.querySelectorAll(`img.${NOTIFICATION_EMAIL_IMAGE_THUMB_CLASS}`)].forEach((thumb) => {
    const deliveryNode = replaceThumbWithDeliveryImage(thumb, failed);
    if (!deliveryNode) return;

    imageCount += 1;
    thumb.replaceWith(deliveryNode);
  });

  return { failed, imageCount };
};

export function prepareEmailHtmlForDelivery(source) {
  const html = typeof source === "string"
    ? source
    : source?.innerHTML || "";

  if (!html?.trim()) {
    return { html: "", failed: [], imageCount: 0 };
  }

  const container = document.createElement("div");
  container.innerHTML = html;

  const { failed, imageCount } = transformEditorImagesForDelivery(container);
  mergeAdjacentDeliveryImageTables(container);

  if (countImageThumbsInHtml(container) > 0 && imageCount === 0) {
    return {
      html: "",
      failed: failed.length
        ? failed
        : [{
            alt: "Image",
            reason: "Attached images could not be read. Upload the image again.",
          }],
      imageCount: 0,
    };
  }

  cleanupDeliveryHtml(container);
  unwrapNestedParagraphs(container);

  return {
    html: container.innerHTML.trim(),
    failed,
    imageCount,
  };
}

export const defaultNotificationImageUploadHandler = async (file) => {
  if (!file?.type?.startsWith("image/")) {
    throw new Error("Only image files are supported");
  }

  try {
    return await uploadNotificationEmailImage(file);
  } catch (uploadError) {
    const apiMessage = uploadError?.response?.data?.message;
    const status = uploadError?.response?.status;
    const statusHint = status ? ` (HTTP ${status})` : "";
    throw new Error(
      apiMessage || uploadError?.message || `Failed to upload image${statusHint}`
    );
  }
};

export const attachEmailImageClickHandlers = (editorEl, onContentChange) => {
  if (!editorEl || editorEl._notificationEmailClickHandlerAttached) return;
  editorEl._notificationEmailClickHandlerAttached = true;

  const handleImageActivate = (nativeEvt) => {
    if (nativeEvt.button !== 0) return;

    const removeButton = nativeEvt.target?.closest?.(`.${NOTIFICATION_EMAIL_IMAGE_REMOVE_CLASS}`);
    if (removeButton) {
      nativeEvt.preventDefault();
      nativeEvt.stopPropagation();
      removeNotificationEmailImageFromDom(editorEl, removeButton, onContentChange);
      return;
    }

    const previewTarget = nativeEvt.target?.closest?.(`img.${NOTIFICATION_EMAIL_IMAGE_THUMB_CLASS}`);
    if (!previewTarget) return;

    nativeEvt.preventDefault();
    nativeEvt.stopPropagation();
    openNotificationImagePreview(getNotificationImagePreviewUrl(previewTarget));
  };

  editorEl.addEventListener("mousedown", handleImageActivate, true);
};
